import {Component, inject, Input, OnDestroy, OnInit, computed, signal} from '@angular/core';
import {Observable, Subscription} from 'rxjs';
import {filter, map, switchMap} from 'rxjs/operators';
import {CoreService} from 'src/app/core/services/core.service';
import {MedicationItemAddCardComponent} from '@xmedical/clinical-shared';
import {MatDialog} from '@angular/material/dialog';
import {MasterDataRepositoryService, MedicationRepositoryService, Medication, Patient, ScriptType, UserSettings} from '@xmedical/core';
import {MedicationAddedEvent, MedicationDisplayItem, MedicationRemovedEvent} from '@xmedical/shared';
import {PatientMedicationStore, PatientScriptsStore, PatientStore} from '../../../core/state';

@Component({
    selector: 'app-patient-medication-widget',
    templateUrl: './medication-widget.component.html',
    styleUrls: ['./medication-widget.component.scss'],
    standalone: false
})
export class MedicationWidgetComponent implements OnInit, OnDestroy {

  @Input() title: string;
  @Input() scriptType: ScriptType;
  error = '';
  loading = false;
  settings: UserSettings;
  private subscriptions: Subscription[] = [];

  private medicationRepository = inject(MedicationRepositoryService);
  private masterDataRepository = inject(MasterDataRepositoryService);
  private matDialog = inject(MatDialog);
  private coreService = inject(CoreService);
  private patientStore = inject(PatientStore);
  protected medicationStore = inject(PatientMedicationStore);
  private scriptsStore = inject(PatientScriptsStore);

  isProcessing = signal(false);

  canAdd = computed(() => this.coreService.hasPermission('Clinical.Medication.Write'));
  canDelete = computed(() => this.coreService.hasPermission('Clinical.Medication.Write'));
  canSaveScript = computed(() => this.coreService.hasPermission('Clinical.Scripts.Write'));

  /** Items filtered to this widget's scriptType. */
  readonly medication = computed<Medication[]>(() =>
    this.medicationStore.items().filter(item => item.scriptType === this.scriptType)
  );

  medicationItems = computed<MedicationDisplayItem[]>(() =>
    this.medication().map(item => ({
      id: item.id,
      description: item.description,
      dosage: item.dosage,
      diagnosisCode: item.diagnosisCode,
      comment: item.comment,
      lastModifiedDate: item.lastModifiedDate,
      lastModifiedUser: item.lastModifiedUser,
      typeId: item.typeId,
      typeUnassigned: item.type?.unassigned,
      textColour: item.textColour
    }))
  );

  get hasError(): boolean {
    return this.error.length > 0;
  }

  get hasMedication(): boolean {
    return this.medication().length > 0;
  }

  get patientId(): number {
    return this.patientStore.current()?.id ?? 0;
  }

  get patient(): Patient | null {
    return this.patientStore.current();
  }

  get confirmationMessage(): string {
    return 'Are you sure you want to save a chronic script with this medication for ' + this.patient?.firstName + ' ' + this.patient?.surname + '?';
  }

  searchMedication$ = (query: string): Observable<Medication[]> => {
    return this.medicationRepository.query(query)
      .pipe(map(response => response));
  }

  ngOnInit(): void {
    this.settings = this.coreService.settings();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  onAddNewItem(searchTerm: string) {
    const dialogRef = this.matDialog.open(MedicationItemAddCardComponent, {
      panelClass: 'mat-confirmation-dialog-responsive',
      width: '700px',
      data: {
        searchTerm,
        diagnosisCodeSearch: (q: string) => this.masterDataRepository.searchDiagnosisCodes(q),
      },
    });

    // Card now returns the form value via dialogRef.close (no id). Persist
    // it to the master medications list to obtain an id, then run the
    // existing patient-attach flow via the medication store.
    this.subscriptions.push(dialogRef.afterClosed().pipe(
      filter((v): v is Medication => !!v),
      switchMap(medication => this.medicationRepository.createMedicationItem(medication).pipe(
        map(id => ({ ...medication, id } as Medication)),
      )),
    ).subscribe(medication => this.onItemSelected(medication)));
  }

  onItemAdded(event: MedicationAddedEvent) {
    const medication = event.item as Medication;
    medication.comment = event.comment;
    this.onItemSelected(medication);
  }

  onItemSelected(medication: Medication) {
    medication.createdUser = this.coreService.currentUser;
    medication.lastModifiedUser = this.coreService.currentUser;
    medication.createdDate = new Date();
    medication.lastModifiedDate = new Date();
    medication.scriptType = this.scriptType;
    this.error = '';
    this.subscriptions.push(this.medicationStore.add(medication).subscribe({
      error: () => {
        this.error = 'Could not add new medication item to this patient';
      }
    }));
  }

  hasDuplicates(item: Medication): boolean {
    return this.medication().some(m => {
      if (m.description.length > item.description.length) {
        return m.description.indexOf(item.description) > -1 && m.id !== item.id;
      } else if (m.description.length < item.description.length) {
        return item.description.indexOf(m.description) > -1 && m.id !== item.id;
      } else {
        return m.description === item.description && m.id !== item.id;
      }
    });
  }

  hasDuplicateTypes(item: Medication): boolean {
    return this.medication().some(m => {
      return !item.type.unassigned && item.typeId === m.typeId && item.id !== m.id;
    });
  }

  saveScript() {
    this.subscriptions.push(
      this.scriptsStore.createFromMedication(this.medication(), this.scriptType).subscribe({
        error: (error) => {
          this.coreService.showErrorToast('Could not save script');
          console.error(error);
        }
      })
    );
  }

  onItemRemoved(event: MedicationRemovedEvent) {
    const id = event.item.id;
    if (!id) return;
    this.isProcessing.set(true);
    this.subscriptions.push(this.medicationStore.remove(id).subscribe({
      next: () => this.isProcessing.set(false),
      error: () => {
        this.coreService.showErrorToast('Could not remove medication item');
        this.isProcessing.set(false);
      }
    }));
  }
}
